import { useNavigateWithQuery } from '@shared/hooks/useNavigateWithQuery';
import { I18nStore } from '@tools/i18n/store';
import { injectTSDI } from '@tools/tsdi';
import {
    type AllowedCountries,
    considerSpecialCountriesMappings,
    getLocaleFromBrowser,
    isCountryAllowed,
    LANGUAGE_PREFERENCE_KEY,
    localesPathMappings
} from '@tools/utils/locale';
import * as React from 'react';
import { useParams } from 'react-router';

interface RouteGuardProps {
    children: React.ReactElement;
}

export const RouteGuard = ({ children }: RouteGuardProps) => {
    const { country } = useParams<{ country: string }>();
    const navigate = useNavigateWithQuery();
    const { setLocale, country: currentCountry } = injectTSDI(I18nStore);

    const countryCode = country?.toUpperCase() as AllowedCountries;

    React.useEffect(() => {
        if (currentCountry === countryCode) {
            return;
        }
        const isValid = countryCode && isCountryAllowed(countryCode);
        if (isValid) {
            const lang = localesPathMappings[countryCode];
            const existingLang = localStorage.getItem(LANGUAGE_PREFERENCE_KEY);
            if (!existingLang) {
                localStorage.setItem(LANGUAGE_PREFERENCE_KEY, lang);
            }
            setLocale({
                lang,
                countryCode: considerSpecialCountriesMappings(countryCode)
            });
        } else {
            const { countryCode } = getLocaleFromBrowser();
            navigate(`/${countryCode.toLowerCase()}`, { replace: true });
        }
    }, [navigate, countryCode, setLocale, currentCountry]);

    return children;
};
